import React from "react";
import { FiCheckCircle } from "react-icons/fi";
import { Modal } from "../Components/Modal";
import { modalManager } from "./ModalManager";
import PublishAfterSaveButton from "../Components/Content/PublishAfterSaveButton";

/**
 * Fire-and-forget success confirmation, pushed onto the global modal stack (see ModalManager) : so
 * it can be called from anywhere, including non-component code, without wiring modal state into the
 * caller. Optional `note` highlights a follow-up the save did NOT do (typically "deploy to see it
 * live"), and `actionLabel`/`onAction` add a secondary button that closes the modal before acting.
 * `publish` adds « Mettre en production » for the content just saved, shown only to people who
 * hold a production right : the save and its publication become one gesture for them.
 */
type SuccessModalOptions = {
	title?: string;
	message?: string;
	/** Shown under the message when present (e.g. "Deploy to preview to see changes live.") */
	note?: string;
	actionLabel?: string;
	onAction?: () => void;
	confirmLabel?: string;
	onClose?: () => void;
	/** Le contenu qui vient d'être enregistré, pour proposer sa mise en production. */
	publish?: {
		contentId: string;
		contentType?: string | null;
		/** Après une publication réussie (la modale est déjà fermée). */
		onPublished?: () => void;
	};
	/**
	 * Variante libre du même geste, pour un écran dont la mise en production ne passe pas par
	 * `publish-content` (les données macroéconomiques, par exemple). `close` ferme la modale.
	 * Le composant rendu porte lui-même son contrôle de permission : il ne rend rien à qui n'a
	 * pas le droit, exactement comme le bouton de contenu.
	 */
	renderPublish?: (close: () => void) => React.ReactNode;
};

export const showSuccessModal = ({
	title = "Success",
	message = "Changes saved successfully.",
	note,
	actionLabel,
	onAction,
	confirmLabel = "Got it",
	onClose,
	publish,
	renderPublish
}: SuccessModalOptions) => {
	modalManager.push(
		(controller) => (
			<>
				<Modal.Header closeButton>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="flex items-start gap-3">
						<div className="rounded-full bg-emerald-100 p-2 text-emerald-600">
							<FiCheckCircle className="h-5 w-5" />
						</div>
						<div>
							<p className="text-sm text-slate-700">{message}</p>
							{note ? <p className="mt-1.5 text-sm font-medium text-amber-700">{note}</p> : null}
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					{publish?.contentId ? (
						<PublishAfterSaveButton
							contentId={publish.contentId}
							contentType={publish.contentType}
							onPublished={() => {
								modalManager.pop(controller.id);
								publish.onPublished?.();
							}}
						/>
					) : null}
					{renderPublish ? renderPublish(() => modalManager.pop(controller.id)) : null}
					{actionLabel ? (
						<button
							type="button"
							className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
							onClick={() => {
								modalManager.pop(controller.id);
								onAction?.();
							}}
						>
							{actionLabel}
						</button>
					) : null}
					<button
						type="button"
						className="inline-flex items-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500"
						onClick={() => modalManager.pop(controller.id)}
					>
						{confirmLabel}
					</button>
				</Modal.Footer>
			</>
		),
		{
			size: "sm",
			onClose
		}
	);
};


